import { useEffect, useState } from 'react';
import {
  describeError,
  fetchRevisionPeople,
  type RevisionPeopleResponse,
  type RevisionPersonRow,
} from '../api.js';
import { formatDate, formatHoursTotal } from '../format.js';

/**
 * Формулировки намеренно про **учётные записи и списания**, а не про людей и
 * работу. Норму часов посчитать не из чего: в Jira нет ни отпусков, ни
 * больничных, а исполнитель задачи здесь означает ответственного, а не того,
 * кто её делает (разведка 2026-09-01). Этот экран показывает, где не работает
 * процесс списания, и не годится для выводов о том, кто сколько работал.
 *
 * Карточек-фильтров по когортам нет (решение 2026-09-11): они пересказывали
 * то, что видно в самих строках. Единственное деление, которое меняет чтение
 * списка, — работает человек или уволен, и оно сделано двумя таблицами.
 */
export function RevisionPeople({ dataVersion }: { dataVersion: number }) {
  const [data, setData] = useState<RevisionPeopleResponse>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    const controller = new AbortController();
    fetchRevisionPeople(controller.signal).then(setData, (cause: unknown) => {
      if (controller.signal.aborted) return;
      setError(describeError(cause));
    });
    return () => controller.abort();
  }, [dataVersion]);

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="empty">Загрузка…</div>;

  const working = data.rows.filter((row) => row.active);
  const left = data.rows.filter((row) => !row.active);

  return (
    <section className="revision">
      <h3 className="people-heading">
        Работают <span className="people-count">{working.length}</span>
      </h3>
      <PeopleTable rows={working} left={false} />

      {/* Уволенные свёрнуты: их вдвое больше, чем работающих, а нужны они
          по одному поводу — разобрать открытые задачи, оставшиеся за ними.
          Вопрос вкладки «где не работает процесс списания» — про работающих. */}
      <details className="people-left">
        <summary className="people-heading">
          Уволены <span className="people-count">{left.length}</span>
        </summary>
        <p className="muted small">
          Показаны, только если за ними что-то осталось — часы, открытые задачи или заведённые
          задачи. Учётки без единого следа в список не попадают.
        </p>
        <PeopleTable rows={left} left />
      </details>

      <div className="muted small">
        «Часов» — списания в наших worklog; «часов на них» — время с открытых задач по данным Jira,
        включая списанное кем-то другим и до начала нашей выгрузки.
      </div>
    </section>
  );
}

/**
 * Одна таблица на обе группы. У уволенных нет колонки «Молчит, дней»: молчание
 * человека, который ушёл, ничего не сигналит, а дату последнего списания
 * и так показывает соседняя колонка.
 */
function PeopleTable({ rows, left }: { rows: RevisionPersonRow[]; left: boolean }) {
  if (rows.length === 0) return <div className="empty">Никого</div>;

  return (
    <div className="matrix-scroll">
      <table className="matrix revision-table">
        <thead>
          <tr>
            <th>Сотрудник</th>
            <th>Учётка</th>
            <th className="num">Часов</th>
            <th className="num">Месяцев</th>
            <th>Последнее списание</th>
            {!left && <th className="num">Молчит, дней</th>}
            <th className="num">Открытых задач</th>
            <th className="num">Часов на них</th>
            <th className="num">Завёл открытых</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.userKey}>
              <td>{row.displayName}</td>
              <td className="muted">{row.userKey}</td>
              <td className="num">{formatHoursTotal(row.seconds)}</td>
              <td className="num">{row.months || '—'}</td>
              <td>{row.lastWorklogAt ? formatDate(row.lastWorklogAt) : '—'}</td>
              {!left && <td className="num">{row.silentDays ?? '—'}</td>}
              <td className="num">{row.openAssigned || '—'}</td>
              <td className="num">{formatHoursTotal(row.openAssignedSeconds)}</td>
              <td className="num">{row.openReported || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
